import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useStateMachine } from "little-state-machine";

import "./InvitedProviders.scss";
import Card from "components/v2/Card/Card";
import Button from "components/v2/Button/Button";
import { ReactComponent as AlertIcon } from "assets/images/iconsV2/alert-circle-grey.svg";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import EmptyState from "components/v2/EmptyState/EmptyState";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import usePermissions from "hooks/usePermissions";
import { useGetProviderOptions } from "api/queries/quotes/useGetProviderOptions";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { useGetQuotations } from "api/queries/quotes/useGetQuotations";
import DynamicScroll from "components/common/DynamicScroll/DynamicScroll";
import useConfirm from "hooks/useConfirm";
import { DefaultProvidersInvitations } from "types/Permissions";
import OverlayLoader from "components/atoms/OverlayLoader/OverlayLoader";
import errorToast from "helpers/errorToast";
import { cancelProviderInvitation } from "api";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import { updateBackgroundLoadingState } from "store/store";
import ManualParcelQuoteDialog from "pages/shipper/ShipmentQuotes/components/ManualParcelQuoteDialog";
import testIds from "constants/testIds";
import InviteProvidersModal from "pages/shipper/ShipmentQuotes/components/InviteProvidersModal";
import { ProviderInviteStatus } from "../../../../../constants";
import { statusPriority } from "../../helpers/invitedProviderHelper";
import InvitedProviderItem from "./components/InvitedProviderItem";

const SHIPMENT_STATUS = ["Delayed", "In transit", "Incomplete", "Delivered", "Auto-closed", "Booked", "Cancelled"];

const providersSelector = (data: any) => {
  if (!data) return [];
  return data.map((provider: any) => ({
    ...provider,
    name: provider.company_name,
  }));
};

function InvitedProviders() {
  const [isRemovingProvider, setIsRemovingProvider] = useState(false);
  const [manualQuoteProvider, setManualQuoteProvider] = useState<any>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const { confirm, ConfirmModal } = useConfirm();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { id: shipmentId } = useParams();
  const { permissions: permissionData } = usePermissions();
  const { email, permissions, company_shipper_settings } = JSON.parse(permissionData || "{}");
  const { default_providers_invitation } = company_shipper_settings || {};

  const { data: providers, isLoading: isLoadingProviders } = useGetProviderOptions({
    shipmentId: shipmentId!,
    options: { enabled: !!shipmentId, select: providersSelector },
  });

  const { data: shipmentData } = useGetShipmentBasic(shipmentId!, {
    enabled: !!shipmentId,
    refetchOnMount: false,
  });

  const { data: quoteDetailsData } = useGetQuoteRequestDetails({
    shipmentId: shipmentId!,
    options: {
      enabled: !!shipmentId,
    },
  });

  const { data: quotationData } = useGetQuotations({
    shipmentId: shipmentId!,
    options: {
      enabled: !!shipmentId,
      refetchOnMount: false,
    },
  });
  const quotations = quotationData?.quotations ?? [];

  const sortedProviders = useMemo(() => {
    if (!providers) return [];
    return providers
      .filter((provider: any) => provider.status !== ProviderInviteStatus.NOT_INVITED)
      .sort(
        (a: any, b: any) =>
          statusPriority[a.status as keyof typeof statusPriority] - statusPriority[b.status as keyof typeof statusPriority],
      );
  }, [providers]);

  const getQuoteExpiration = (name: string) => {
    if (!quotations) return;
    let hasExpired = false;
    quotations.forEach((quote: any) => {
      if (quote.provider.name === name) hasExpired = moment(quote.general.quote_expiration).isBefore(moment());
    });
    return hasExpired;
  };

  const hasWinner = sortedProviders.some((provider: any) => provider.status === ProviderInviteStatus.WON);
  const isOwnQuoteRequest = shipmentData?.organization?.requestor_email === email;
  const isPermissionEditQuoteRequest = permissions?.quoting?.edit_other_quote_requests;
  const isCanceled = shipmentData?.shipment?.request_status === "Canceled";
  const isRequest = !SHIPMENT_STATUS.includes(quoteDetailsData?.organization?.request_status);
  const hasQuotingDeadlinePassed = shipmentData?.organization?.quoting_deadline_seconds_remaining < 0;
  const hasOneQuotationInApproval = quotations.some((quote: any) => quote.status === "require_approval");

  const canInviteProviders =
    (isOwnQuoteRequest || isPermissionEditQuoteRequest) && !isCanceled && isRequest && !hasQuotingDeadlinePassed;

  const allProvidersInvited =
    !!providers?.length && providers.every((p: any) => p.status !== ProviderInviteStatus.NOT_INVITED);

  const canRemoveProvider = (provider: any) =>
    (([ProviderInviteStatus.QUOTED, ProviderInviteStatus.IN_PROGRESS, ProviderInviteStatus.INVITED].includes(
      provider.status,
    ) &&
      !provider.can_submit_manual_parcel &&
      isRequest &&
      !hasWinner &&
      !getQuoteExpiration(provider.company_name) &&
      isPermissionEditQuoteRequest &&
      !isCanceled) ||
      (isOwnQuoteRequest && !provider.can_submit_manual_parcel && !isCanceled)) &&
    default_providers_invitation !== DefaultProvidersInvitations.PROVIDERS_INVITATION_ALL_MANDATORY &&
    !hasOneQuotationInApproval;

  const canManuallyAddQuote = (provider: any) =>
    (provider.can_submit_manual_parcel || provider.can_submit_manual_quote) && !isCanceled;

  const refetchQuotations = () => {
    actions.updateBackgroundLoadingState(true);
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(shipmentId!) });
  };

  const handleProviderRemove = async (provider: any) => {
    const response = await confirm({
      title: intl.formatMessage({
        id: "quote-request.invitedProviders.removeProvider",
        defaultMessage: "Remove provider",
      }),
      message: intl.formatMessage({
        id: "quote-request.invitedProviders.removeProviderMessage",
        defaultMessage: "Are you sure you want to remove this provider?",
      }),
    });
    if (response) {
      setIsRemovingProvider(true);
      cancelProviderInvitation(shipmentId, provider.id)
        .then(() => {
          setIsRemovingProvider(false);
          toast("Provider invitation canceled", { type: "success" });
          queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteProviderOptionsListing(shipmentId!) });
          refetchQuotations();
        })
        .catch((err) => {
          setIsRemovingProvider(false);
          errorToast(err);
        });
    }
  };

  const handleManuallyAddQuote = (provider: any) => {
    setManualQuoteProvider(provider);
  };

  return (
    <div className="position-relative" data-testid={testIds.SHIPMENT_DASHBOARD_INVITED_PROVIDERS_SECTION}>
      <Card borderRadius="md">
        <Card.Header
          title={intl.formatMessage({
            id: "quote-request.invitedProviders",
            defaultMessage: "Invited Providers",
          })}
          showBorder
          actions={
            !isLoadingProviders &&
            canInviteProviders && (
              <Button
                label={intl.formatMessage({
                  id: "quote-request.invitedProviders.inviteProvider",
                  defaultMessage: "Invite provider",
                })}
                variant="link-color"
                iconLeft={<PlusIcon />}
                onClick={() => {
                  setShowInviteDialog(true);
                }}
                disabled={allProvidersInvited}
                data-testid={testIds.SHIPMENT_DASHBOARD_OPEN_INVITE_PROVIDER_MODAL_BUTTON}
              />
            )
          }
        />
        {isLoadingProviders && <LoadingSkeleton rows={2} />}
        {!isLoadingProviders && sortedProviders?.length > 0 && (
          <DynamicScroll maxHeightBeforeScroll={40}>
            {sortedProviders.map((provider: any, index: number) => (
              <InvitedProviderItem
                key={provider.id}
                providerName={provider.company_name}
                providerLogo={provider.company_logo}
                providerTeam={provider.claimed_team_name}
                status={provider.status}
                hideDivider={index === sortedProviders.length - 1}
                invitedDate={
                  provider.status === ProviderInviteStatus.INVITED
                    ? formatDate(provider.invited_at, DateTimeFormat.dateFormat)
                    : undefined
                }
                onRemoveProvider={canRemoveProvider(provider) ? () => handleProviderRemove(provider) : undefined}
                onManuallyAddQuote={canManuallyAddQuote(provider) ? () => handleManuallyAddQuote(provider) : undefined}
              />
            ))}
          </DynamicScroll>
        )}
        {!isLoadingProviders && !sortedProviders?.length && (
          <Card.Body>
            <EmptyState
              noActions
              customIcon={
                <div className="">
                  <AlertIcon />
                </div>
              }
              title={intl.formatMessage({
                id: "general.couldNotLoadDetails",
                defaultMessage: "We couldn't load these details",
              })}
              description={intl.formatMessage({
                id: "general.tryAgainOrCheckBackLater",
                defaultMessage: "You can try again or check back later.",
              })}
            />
          </Card.Body>
        )}
      </Card>
      <ConfirmModal />
      {isRemovingProvider && <OverlayLoader />}
      {manualQuoteProvider && (
        <ManualParcelQuoteDialog
          isParcel={manualQuoteProvider.can_submit_manual_parcel}
          isManualQuote={manualQuoteProvider.can_submit_manual_quote}
          refetchQuotes={refetchQuotations}
          providerId={manualQuoteProvider.id}
          shipmentId={shipmentId}
          isOpen={!!manualQuoteProvider}
          close={() => setManualQuoteProvider(null)}
        />
      )}
      {!isLoadingProviders && providers?.length > 0 && (
        <InviteProvidersModal
          shipmentId={shipmentId!}
          providers={providers}
          isShowing={showInviteDialog}
          close={() => {
            setShowInviteDialog(false);
          }}
        />
      )}
    </div>
  );
}

export default InvitedProviders;
